import { DocumentType } from 'models/documents/DocumentType';
import type { EmailServiceDocument } from 'models/emailService/EmailServiceDocument';
import type { EmailServicePreviewContent } from 'models/emailService/EmailServicePreviewState';
import type { EmailServiceRecipient } from 'models/emailService/EmailServiceRecipient';
import { getBlotterItemDocuments } from 'state/blotters/blotterPanel/blotterPanel.selector';
import type { AppState } from 'state/state.model';

import type { EmailServiceState } from '.';
import { translateToEmailServiceDocumentType } from './emailService.helpers';

const emptyArray: never[] = [];

const getEmailServiceState = (state: AppState): EmailServiceState => state.emailService;

// TODO: Unit test
export const getEmailServiceModalStatus = (state: AppState) => getEmailServiceState(state).status;

// TODO: Unit test
export const isEmailServiceModalOpen = (state: AppState): boolean => getEmailServiceModalStatus(state) !== 'INACTIVE';

// TODO: Unit test
export const getEmailServiceEmailRecipient = (appState: AppState): EmailServiceRecipient => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.recipient : 'Client';
};

export const isEmailSendToMe = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' && state.recipient === 'Me';
};

// TODO: Unit test
export const getEmailServiceEmailSubject = (appState: AppState): string => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.subject : '';
};

// TODO: Unit test
export const getEmailServiceEmailDocuments = (appState: AppState): readonly EmailServiceDocument[] => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.documents : emptyArray;
};

// TODO: Unit test
export const isEmailServiceFormValid = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.documents.length > 0 && state.preview.emails.length > 0 : false;
};

// TODO: Unit test
export const getEmailServiceAvailableDocuments = (appState: AppState): readonly EmailServiceDocument[] => {
  const allDocuments = getBlotterItemDocuments(appState);
  const availableDocumentTypes: DocumentType[] = ['TermSheet', 'MarketingMaterial'];
  return (
    allDocuments?.docs
      .values()
      .filter(doc => availableDocumentTypes.includes(doc.type))
      .flatMap(
        doc =>
          doc.items
            ?.values()
            .filter(item => item.format === 'PDF')
            .map(item => ({
              type: translateToEmailServiceDocumentType(doc.type),
              language: item.language,
              status: item.status,
            })) ?? emptyArray,
      )
      .toArray() ?? emptyArray
  );
};

// TODO: Unit test
export const isEmailServiceSendingEmail = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.sendStatus === 'SENDING' : false;
};
export const isEmailServiceSentEmailWithoutWarnings = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE'
    ? state.sendStatus === 'SENT' && state.warnings?.length === 0 && state.errors?.length === 0
    : false;
};
export const isEmailServiceSentEmailWithWarnings = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE'
    ? state.sendStatus === 'SENT' && state.warnings?.length > 0 && state.errors?.length === 0
    : false;
};
export const isEmailServiceSubmissionFailed = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.sendStatus === 'SUBMISSION_FAILED' : false;
};

export const getWarnings = (appState: AppState): readonly string[] => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.warnings : emptyArray;
};

export const getErrors = (appState: AppState): readonly string[] => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.errors : emptyArray;
};

export const isLoadingPreview = (appState: AppState): boolean => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.preview.isLoading : false;
};

export const getPreviewEmails = (appState: AppState): readonly EmailServicePreviewContent[] => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? (state.preview.emails ?? emptyArray) : emptyArray;
};

export const getPreviewErrors = (appState: AppState) => {
  const state = getEmailServiceState(appState);
  return state.status === 'ACTIVE' ? state.preview.errors : emptyArray;
};

export const shouldShowLoadingSkeleton = (appState: AppState) => {
  return isLoadingPreview(appState) || getPreviewEmails(appState).length < 1;
};
